import { PaymentMethod } from '../models/Organization';
import { HostRoomBooking } from '../models/HostRoomBooking';
import { PaymentStatus } from '../models/RoomBooking';
import { hostBookingGroupsAsPendingSeries } from './hostRoomBookingGrouping';

export type SeriesAcceptPlan =
  | { kind: 'series' }
  | { kind: 'selected-online'; selectedIds: string[] }
  | { kind: 'selected-each'; selectedIds: string[] };

export function isOnlinePaymentHold(paymentStatus: string | null | undefined): boolean {
  return (
    paymentStatus === PaymentStatus.AMOUNT_BLOCKED ||
    paymentStatus === PaymentStatus.BLOCK_REQUESTED
  );
}

/**
 * All checked and no overlap: accept the series.
 * Online subset: capture the selection and decline the rest.
 * Cash subset: accept each checked session and leave the rest pending.
 */
export function planSeriesAccept(options: {
  memberIds: string[];
  selectedIds: string[];
  conflictIds: string[];
  online: boolean;
}): SeriesAcceptPlan | null {
  const members = new Set(options.memberIds);
  const conflicts = new Set(options.conflictIds);
  const selected = options.selectedIds.filter((id) => members.has(id) && !conflicts.has(id));
  if (selected.length === 0) return null;
  const hasConflict = options.memberIds.some((id) => conflicts.has(id));
  if (selected.length === options.memberIds.length && !hasConflict) {
    return { kind: 'series' };
  }
  if (options.online) return { kind: 'selected-online', selectedIds: selected };
  return { kind: 'selected-each', selectedIds: selected };
}

/** A still-pending online rehearsal cannot be accepted or declined on its own. */
export function pendingOnlineRoomUsesSeriesEndpoint(
  booking: HostRoomBooking,
  now: Date = new Date(),
): boolean {
  if (booking.paymentMethod !== PaymentMethod.ONLINE) return false;
  if (!booking.recurringSeriesId?.trim()) return false;
  return hostBookingGroupsAsPendingSeries(booking, now);
}

export function declineTargetsUseSeriesEndpoint(targets: HostRoomBooking[]): boolean {
  if (targets.length === 0) return false;
  const seriesId = targets[0].recurringSeriesId?.trim();
  if (!seriesId) return false;
  if (!targets.every((booking) => booking.recurringSeriesId?.trim() === seriesId)) return false;
  if (targets.length > 1) return true;
  return pendingOnlineRoomUsesSeriesEndpoint(targets[0]);
}

export function bookingIdsMentionedInError(error: unknown, knownIds: string[]): string[] {
  const known = new Set(knownIds);
  const text = typeof error === 'string' ? error : JSON.stringify(error ?? '');
  const matches =
    text.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g) ??
    [];
  const found: string[] = [];
  const seen = new Set<string>();
  for (const match of matches) {
    if (!known.has(match) || seen.has(match)) continue;
    seen.add(match);
    found.push(match);
  }
  return found;
}
